import { useEffect, useState } from 'react';
import { api, formatErr } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PageHeader, PageShell } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Save, Calendar, Eye, Clock } from 'lucide-react';
import JoiningsTab from '@/components/JoiningsTab';

// Weekly range helpers — SHRS week is Monday to Saturday
function currentWeekRange() {
  const t = new Date();
  const dow = t.getDay(); // 0 = Sun
  const daysFromMonday = (dow + 6) % 7; // Sun=6, Mon=0…
  const mon = new Date(t); mon.setDate(t.getDate() - daysFromMonday);
  const sat = new Date(mon); sat.setDate(mon.getDate() + 5);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(mon), to: iso(sat) };
}
function lastWeekRange() {
  const cw = currentWeekRange();
  const mon = new Date(cw.from); mon.setDate(mon.getDate() - 7);
  const sat = new Date(mon); sat.setDate(mon.getDate() + 5);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(mon), to: iso(sat) };
}
function daysRange(days) {
  const t = new Date();
  const f = new Date(t); f.setDate(t.getDate() - (days - 1));
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(f), to: iso(t) };
}
function monthsRange(months) {
  const t = new Date();
  const f = new Date(t); f.setMonth(t.getMonth() - months);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(f), to: iso(t) };
}
const REPORT_RANGES = [
  { key: 'today', label: 'Today', get: () => daysRange(1) },
  { key: 'this_week', label: 'This Week (Mon–Sat)', get: currentWeekRange },
  { key: 'last_week', label: 'Last Week', get: lastWeekRange },
  { key: '15d', label: '15 days', get: () => daysRange(15) },
  { key: '30d', label: '30 days', get: () => daysRange(30) },
  { key: '3m', label: '3 months', get: () => monthsRange(3) },
];

export default function DailyReport() {
  const { user } = useAuth();
  const canViewAll = ['admin', 'manager'].includes(user?.role);
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [portals, setPortals] = useState([]);
  const [form, setForm] = useState({
    calls_made: 0, lineups: 0, interviews_processed: 0, offers_rolled: 0, joinings: 0,
    portal_breakdown: {}, upcoming_joinings: [], notes: '',
  });
  const [recent, setRecent] = useState([]);

  // Team view state
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState('all');
  const [rangeKey, setRangeKey] = useState('this_week');
  const initialRange = REPORT_RANGES.find(r => r.key === 'this_week').get();
  const [monthStart, setMonthStart] = useState(initialRange.from);
  const [monthEnd, setMonthEnd] = useState(initialRange.to);
  const [teamRows, setTeamRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null); // { user_id, date }

  const loadMine = async () => {
    try {
      const { data } = await api.get('/reports', { params: {} });
      setRecent(data.slice(0, 7));
    } catch (e) { toast.error(formatErr(e)); }
  };

  const loadTeam = async () => {
    try {
      const params = { from_date: monthStart, to_date: monthEnd };
      if (userFilter !== 'all') params.user_id = userFilter;
      const { data } = await api.get('/reports', { params });
      setTeamRows(data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const loadForDate = async (d) => {
    try {
      const { data } = await api.get(`/reports/mine/${d}`);
      if (data) {
        setForm({
          calls_made: data.calls_made, lineups: data.lineups,
          interviews_processed: data.interviews_processed,
          offers_rolled: data.offers_rolled, joinings: data.joinings,
          portal_breakdown: data.portal_breakdown || {},
          upcoming_joinings: data.upcoming_joinings || [],
          notes: data.notes || '',
        });
      } else {
        setForm({ calls_made: 0, lineups: 0, interviews_processed: 0, offers_rolled: 0, joinings: 0, portal_breakdown: {}, upcoming_joinings: [], notes: '' });
      }
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    api.get('/portals').then(({ data }) => setPortals(data)).catch(() => {});
    loadMine();
    if (canViewAll) api.get('/users').then(({ data }) => setUsers(data)).catch(() => {});
  }, [canViewAll]);

  useEffect(() => { loadForDate(date); }, [date]);
  useEffect(() => { if (canViewAll) loadTeam(); /* eslint-disable-next-line */ }, [userFilter, monthStart, monthEnd, canViewAll]);

  const setNum = (k, v) => setForm(f => ({ ...f, [k]: Math.max(0, parseInt(v || 0, 10)) }));
  const setPortal = (p, v) => setForm(f => ({ ...f, portal_breakdown: { ...f.portal_breakdown, [p]: Math.max(0, parseInt(v || 0, 10)) } }));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/reports', { date, ...form });
      toast.success('Report saved');
      loadMine();
      if (canViewAll) loadTeam();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const portalTotal = Object.values(form.portal_breakdown).reduce((a, b) => a + (parseInt(b, 10) || 0), 0);

  const submitTab = (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4">
        <div className="tactical-card p-6">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-4">Core metrics · {user?.name}</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              ['calls_made', 'Calls Made'], ['lineups', 'Lineups'], ['interviews_processed', 'Interviews'], ['offers_rolled', 'Offers'], ['joinings', 'Joinings'],
            ].map(([k, label]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
                <Input data-testid={`field-${k}`} type="number" min="0" value={form[k]} onChange={(e) => setNum(k, e.target.value)} className="h-11 font-display text-xl font-black" />
              </div>
            ))}
          </div>
        </div>

        <div className="tactical-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Portal-wise calls</div>
            <div className="text-xs text-muted-foreground">Total: <span className="font-display font-black text-foreground">{portalTotal}</span></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {portals.map(p => (
              <div key={p} className="flex items-center gap-3 border border-border/70 rounded-md p-3">
                <div className="flex-1 text-sm font-medium">{p}</div>
                <Input data-testid={`portal-${p.toLowerCase()}`} type="number" min="0" value={form.portal_breakdown[p] || 0}
                  onChange={(e) => setPortal(p, e.target.value)} className="w-20 h-9 text-right font-display font-bold" />
              </div>
            ))}
          </div>
        </div>

        <div className="tactical-card p-6">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Upcoming joinings</div>
          <div className="text-sm text-muted-foreground">Please add candidate offers & joining dates in the <span className="font-medium text-foreground">Joinings</span> tab above — they'll be tracked with proper status (Pending → Joined / Postponed / Dropped) and shown on the dashboard.</div>
        </div>

        <div className="tactical-card p-6">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes / blockers</Label>
          <Textarea data-testid="field-notes" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} rows={4} className="mt-2" placeholder="Any wins, blockers or notes for your manager…" />
        </div>

        <Button data-testid="report-submit" onClick={submit} disabled={busy} size="lg" className="bg-primary hover:bg-primary/90 text-white">
          <Save className="w-4 h-4 mr-2" /> {busy ? 'Saving…' : 'Save Report'}
        </Button>
      </div>

      <div className="tactical-card p-6 h-fit">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Recent reports</div>
        <div className="space-y-2">
          {recent.map(r => (
            <button key={r.id} onClick={() => setDate(r.date)} className="w-full text-left p-3 rounded-md border border-border/70 hover:border-primary transition-colors">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">{new Date(r.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</div>
                <div className="font-display text-lg font-black text-primary">{r.calls_made}</div>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">{r.lineups} lineups · {r.interviews_processed} interviews</div>
            </button>
          ))}
          {recent.length === 0 && <div className="text-sm text-muted-foreground">No history yet.</div>}
        </div>
      </div>
    </div>
  );

  const teamTab = (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="report-range-chips">
        {REPORT_RANGES.map(r => (
          <button key={r.key} data-testid={`report-range-${r.key}`} onClick={() => {
            const { from, to } = r.get();
            setRangeKey(r.key); setMonthStart(from); setMonthEnd(to);
          }}
            className={`px-3 py-1.5 rounded-md text-[11px] uppercase tracking-widest border transition-colors ${rangeKey === r.key ? 'bg-primary text-white border-primary' : 'border-border/70 hover:border-primary text-muted-foreground'}`}>
            {r.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <Label className="text-[10px] uppercase tracking-widest">Recruiter</Label>
          <Select value={userFilter} onValueChange={setUserFilter}>
            <SelectTrigger className="w-56" data-testid="team-user-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All recruiters</SelectItem>
              {users.filter(u => u.role !== 'admin').map(u => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest">From</Label>
          <Input data-testid="team-from" type="date" value={monthStart} onChange={(e) => { setMonthStart(e.target.value); setRangeKey('custom'); }} className="w-40 h-10" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest">To</Label>
          <Input data-testid="team-to" type="date" value={monthEnd} onChange={(e) => { setMonthEnd(e.target.value); setRangeKey('custom'); }} className="w-40 h-10" />
        </div>
        <div className="ml-auto text-xs text-muted-foreground">Showing <span className="font-display font-black text-foreground">{teamRows.length}</span> report{teamRows.length === 1 ? '' : 's'}</div>
      </div>

      <div className="tactical-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Recruiter</TableHead>
              <TableHead className="text-right">Calls</TableHead>
              <TableHead className="text-right">Lineups</TableHead>
              <TableHead className="text-right">Interviews</TableHead>
              <TableHead className="text-right">Offers</TableHead>
              <TableHead className="text-right">Joinings</TableHead>
              <TableHead className="text-right">Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="team-reports-tbody">
            {teamRows.map(r => (
              <TableRow key={r.id}>
                <TableCell className="text-sm">{new Date(r.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</TableCell>
                <TableCell className="font-medium">{r.user_name}</TableCell>
                <TableCell className="text-right font-display font-black text-primary">{r.calls_made}</TableCell>
                <TableCell className="text-right font-display font-black">{r.lineups}</TableCell>
                <TableCell className="text-right">{r.interviews_processed}</TableCell>
                <TableCell className="text-right">{r.offers_rolled}</TableCell>
                <TableCell className="text-right">{r.joinings}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => setDetail({ user_id: r.user_id, date: r.date })} data-testid={`view-${r.id}`}>
                    <Eye className="w-3 h-3 mr-1" /> View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {teamRows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No reports in this range.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  return (
    <PageShell>
      <PageHeader
        eyebrow="Daily update"
        title={canViewAll ? "Reports" : "Submit today's report"}
        subtitle={canViewAll ? "Submit your own report or drill into any teammate's numbers, day by day." : "Log your calls, lineups, interviews and portal-wise breakdown. Keeps the team leaderboard live."}
        actions={
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <Input data-testid="report-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" />
          </div>
        }
      />

      {canViewAll ? (
        <Tabs defaultValue="team">
          <TabsList data-testid="report-tabs">
            <TabsTrigger value="team" data-testid="tab-team">Team Reports</TabsTrigger>
            <TabsTrigger value="mine" data-testid="tab-mine">Submit My Report</TabsTrigger>
            <TabsTrigger value="joinings" data-testid="tab-joinings">Joinings</TabsTrigger>
          </TabsList>
          <TabsContent value="team" className="mt-4">{teamTab}</TabsContent>
          <TabsContent value="mine" className="mt-4">{submitTab}</TabsContent>
          <TabsContent value="joinings" className="mt-4"><JoiningsTab /></TabsContent>
        </Tabs>
      ) : (
        <Tabs defaultValue="mine">
          <TabsList data-testid="report-tabs">
            <TabsTrigger value="mine" data-testid="tab-mine">Submit My Report</TabsTrigger>
            <TabsTrigger value="joinings" data-testid="tab-joinings">Joinings</TabsTrigger>
          </TabsList>
          <TabsContent value="mine" className="mt-4">{submitTab}</TabsContent>
          <TabsContent value="joinings" className="mt-4"><JoiningsTab /></TabsContent>
        </Tabs>
      )}

      <ReportDetailDialog detail={detail} onClose={() => setDetail(null)} />
    </PageShell>
  );
}

function ReportDetailDialog({ detail, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!detail) { setData(null); return; }
    api.get(`/reports/detail/${detail.user_id}/${detail.date}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [detail]);
  if (!detail) return null;

  const r = data?.report;
  const a = data?.attendance;
  const portalEntries = r?.portal_breakdown ? Object.entries(r.portal_breakdown).filter(([, v]) => v > 0) : [];
  const inTime = a?.punch_in ? new Date(a.punch_in).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—';
  const outTime = a?.punch_out ? new Date(a.punch_out).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <Dialog open={!!detail} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="report-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black">
            {data?.user?.name || 'Report'} <span className="text-muted-foreground font-normal text-base">· {new Date(detail.date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
          </DialogTitle>
          {data?.user?.designation && <div className="text-xs text-muted-foreground">{data.user.designation} · {data.user.email}</div>}
        </DialogHeader>

        {!r && !a && <div className="text-center text-muted-foreground py-8">No data submitted for this day.</div>}

        {r && (
          <div>
            <div className="grid grid-cols-5 gap-3">
              {[['Calls', r.calls_made], ['Lineups', r.lineups], ['Interviews', r.interviews_processed], ['Offers', r.offers_rolled], ['Joinings', r.joinings]].map(([l, v]) => (
                <div key={l} className="tactical-card p-3 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div>
                  <div className="font-display text-2xl font-black">{v}</div>
                </div>
              ))}
            </div>
            {portalEntries.length > 0 && (
              <div className="mt-4">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Portal-wise calls</div>
                <div className="flex flex-wrap gap-2">
                  {portalEntries.map(([k, v]) => (<Badge key={k} variant="outline" className="text-xs">{k}: <span className="ml-1 font-display font-black">{v}</span></Badge>))}
                </div>
              </div>
            )}
            {r.notes && (
              <div className="mt-4">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Notes</div>
                <div className="text-sm bg-muted/40 rounded-md p-3">{r.notes}</div>
              </div>
            )}
          </div>
        )}

        <div className="mt-4 tactical-card p-4">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground mb-2"><Clock className="w-3 h-3" /> Attendance for this day</div>
          {a ? (
            <div className="flex items-center gap-6 text-sm">
              <div><span className="text-muted-foreground">In:</span> <span className="font-display font-black">{inTime}</span></div>
              <div><span className="text-muted-foreground">Out:</span> <span className="font-display font-black">{outTime}</span></div>
              <div><span className="text-muted-foreground">Hours:</span> <span className="font-display font-black">{a.hours ?? '—'}</span></div>
            </div>
          ) : <div className="text-sm text-muted-foreground">No punch-in record for this day.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
